import { useState } from 'react';
import { toast } from 'sonner';
import { RotateCcw } from 'lucide-react';
import { Button } from '../../ui/Button';
import { ApiError } from '../../../lib/api';
import { useResetTrainingProgress } from '../../../hooks/useTrainingProgress';

/**
 * "Reset and re-assign" for one person on one module. Clears their saved
 * draft, voids this module's attempts and assigns it again, so the next
 * open starts fresh on the current published version. Two-step so a
 * mis-click in the attempt-history modal cannot wipe someone's progress;
 * the optional reason lands on the audit event. Other modules are untouched.
 */
export function ResetProgressSection({
  personId,
  taskId,
  complete,
  onDone,
}: {
  personId: string;
  taskId: string;
  /** True when the module is already complete (wording differs). */
  complete: boolean;
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const reset = useResetTrainingProgress();

  function submit() {
    reset.mutate(
      { personId, taskId, reason },
      {
        onSuccess: () => {
          toast.success('Progress reset; the module is assigned again from the current version');
          onDone();
        },
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not reset progress'),
      },
    );
  }

  if (!confirming) {
    return (
      <div className="mt-4 border-t border-line/10 pt-3">
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
          onClick={() => setConfirming(true)}
        >
          Reset and re-assign
        </Button>
        <p className="mt-1.5 text-xs text-fg-dim">
          {complete
            ? 'Asks them to do this module again from the start, on the current published version.'
            : 'Discards their saved progress so they start again on the current published version.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2 rounded-token-sm border border-warning-soft/30 bg-warning-soft/10 p-3">
      <p className="text-sm text-fg">
        This clears their saved answers for this module{complete ? ', voids the completion' : ''}{' '}
        and assigns it again as due. Only this module is affected. It cannot be undone from here.
      </p>
      <label className="block text-xs font-medium text-fg-muted">
        Reason (optional, kept on the audit trail)
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder="e.g. questionnaire updated 11 Sep"
          className="mt-1 w-full rounded-token-sm border border-line/20 bg-surface px-2.5 py-1.5 text-sm text-fg"
        />
      </label>
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirming(false)}
          disabled={reset.isPending}
        >
          Cancel
        </Button>
        <Button variant="destructive" size="sm" loading={reset.isPending} onClick={submit}>
          Reset and re-assign
        </Button>
      </div>
    </div>
  );
}
